import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../api";
import { useT } from "../i18n";
import { tg } from "../telegram";
import type { Folder, Me } from "../types";

export default function FilesPage({ me }: { me: Me }) {
  const { t } = useT();
  const navigate = useNavigate();
  const isAdmin = me.role === "admin";
  const [folders, setFolders] = useState<Folder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get<Folder[]>("/api/folders").then(setFolders).catch((e: ApiError) => setError(e.message));
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    const n = name.trim();
    if (!n || busy) return;
    tg()?.HapticFeedback?.impactOccurred("light");
    setBusy(true);
    try {
      await api.post("/api/folders", { name: n });
      setName("");
      load();
    } catch (e) {
      const m = e instanceof ApiError ? e.message : t("common.error");
      const a = tg()?.showAlert;
      if (a) a(m);
      else window.alert(m);
    } finally {
      setBusy(false);
    }
  };

  const remove = (f: Folder, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const go = async () => {
      setBusyId(f.id);
      try {
        await api.delete(`/api/folders/${f.id}`);
        load();
      } catch (err) {
        const m = err instanceof ApiError ? err.message : t("common.error");
        const a = tg()?.showAlert;
        if (a) a(m);
        else window.alert(m);
      } finally {
        setBusyId(null);
      }
    };
    tg()?.HapticFeedback?.impactOccurred("light");
    const c = tg()?.showConfirm;
    if (c) c(t("files.confirm_del_folder"), (ok) => ok && go());
    else if (window.confirm(t("files.confirm_del_folder"))) go();
  };

  return (
    <div className="page">
      <header className="app-header">
        <h1 className="app-title">📁 {t("files.title")}</h1>
      </header>

      <div className="card">
        <div className="card-title">{t("files.new_folder")}</div>
        <div className="row-inline">
          <input
            className="search"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("files.folder_name")}
          />
          <button className="btn small" onClick={create} disabled={busy || !name.trim()}>
            {t("files.create")}
          </button>
        </div>
      </div>

      {error && <div className="alert error">{error}</div>}
      {!folders && !error && (
        <div className="empty">
          <span className="spinner" />
        </div>
      )}
      {folders && folders.length === 0 && <div className="empty">{t("files.empty_folders")}</div>}

      <div className="doc-list">
        {folders?.map((f) => (
          <div key={f.id} className="doc-card">
            <div className="folder-main" onClick={() => navigate(`/files/${f.id}`)}>
              <span className="folder-ico">📁</span>
              <div className="doc-card-main">
                <div className="doc-card-title">{f.name}</div>
                <div className="doc-card-meta">{t("files.count", { n: f.file_count })}</div>
              </div>
            </div>
            {isAdmin ? (
              <button
                className="chip danger"
                disabled={busyId === f.id}
                onClick={(e) => remove(f, e)}
                title={t("files.delete")}
              >
                🗑
              </button>
            ) : (
              <span className="doc-card-arrow" onClick={() => navigate(`/files/${f.id}`)}>
                ›
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
